import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiGitBranch, FiUser, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import { fetchAlerts, updateAlert, assignAlert } from '../../utils/sentimentApi';

const PRIORITY_COLORS = { P1: 'sm-badge-p1', P2: 'sm-badge-p2', P3: 'sm-badge-p3' };
const STATUS_COLORS    = {
  open: 'sm-badge-open', acknowledged: 'sm-badge-acknowledged',
  resolved: 'sm-badge-resolved', escalated: 'sm-badge-escalated',
};

export default function SentimentAlerts({ onOpenWorkflow, showToast }) {
  const [alerts, setAlerts]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStatus,   setFilterStatus]   = useState('open');

  const [assignModal, setAssignModal]   = useState(null); // alert object
  const [assignName, setAssignName]     = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterPriority) params.priority = filterPriority;
      const data = await fetchAlerts(params);
      let items = Array.isArray(data) ? data : (data.items || []);
      if (filterStatus) items = items.filter(a => a.status === filterStatus);
      setAlerts(items);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filterPriority, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const userEmail = localStorage.getItem('sentiment_user_email') || '';

  const handleStatus = async (alert, status) => {
    setActionLoading(true);
    try {
      const body = { status };
      if (status === 'resolved' && userEmail) body.resolved_by = userEmail;
      await updateAlert(alert.alert_id, body);
      setAlerts(prev => prev.map(a => a.alert_id === alert.alert_id ? { ...a, status } : a));
      showToast?.(`Alert ${status}`, 'success');
    } catch (e) {
      setError(e.message);
      showToast?.(e.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!assignName.trim()) return;
    setActionLoading(true);
    try {
      await assignAlert(assignModal.alert_id, assignName.trim());
      setAlerts(prev => prev.map(a => a.alert_id === assignModal.alert_id ? { ...a, assigned_to: assignName.trim() } : a));
      setAssignModal(null);
      setAssignName('');
      showToast?.(`Assigned to ${assignName.trim()}`, 'success');
    } catch (e) {
      setError(e.message);
      showToast?.(e.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div>
      <div className="sm-section-header">
        <h2 className="sm-section-title">Alerts ({alerts.length})</h2>
        <div className="sm-filters">
          <select className="sm-select" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
            <option value="">All Priority</option>
            <option value="P1">P1</option>
            <option value="P2">P2</option>
            <option value="P3">P3</option>
          </select>
          <select className="sm-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All Status</option>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="escalated">Escalated</option>
            <option value="resolved">Resolved</option>
          </select>
          <button className="sm-btn sm-btn-ghost" onClick={load} title="Refresh">
            <FiRefreshCw size={14} />
          </button>
        </div>
      </div>

      {error && (
        <div className="sm-alert-err"><FiAlertTriangle /> {error}</div>
      )}

      {!loading && alerts.length === 0 ? (
        <div className="sm-empty"><div className="sm-empty-icon">🔔</div><span>No alerts found.</span></div>
      ) : (
        <div className="sm-table-wrap">
          <table className="sm-table sm-table-clickable">
            <thead>
              <tr>
                <th>Alert ID</th>
                <th>Customer</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Assigned To</th>
                <th>Reason</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(7)].map((_, i) => (
                  <tr key={i} className="sm-skel-row">
                    <td><div className="sm-skel" style={{ width: '62%' }} /></td>
                    <td><div className="sm-skel" style={{ width: '70%' }} /></td>
                    <td><div className="sm-skel" style={{ width: '36%' }} /></td>
                    <td><div className="sm-skel" style={{ width: '55%' }} /></td>
                    <td><div className="sm-skel" style={{ width: '60%' }} /></td>
                    <td><div className="sm-skel" style={{ width: '80%' }} /></td>
                    <td><div className="sm-skel" style={{ width: '50%' }} /></td>
                    <td><div className="sm-skel" style={{ width: '66%' }} /></td>
                  </tr>
                ))
              ) : alerts.map((alert, i) => (
                <tr
                  key={alert.alert_id}
                  style={{ '--row-delay': `${i * 30}ms` }}
                  className={alert.priority === 'P1' && alert.status === 'open' ? 'sm-row-p1' : ''}
                >
                  <td>
                    <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--sm-muted)' }}>
                      {alert.alert_id?.slice(0, 16)}…
                    </span>
                  </td>
                  <td style={{ fontWeight: 600 }}>{alert.customer_id}</td>
                  <td><span className={`sm-badge ${PRIORITY_COLORS[alert.priority] || ''}`}>{alert.priority}</span></td>
                  <td><span className={`sm-badge ${STATUS_COLORS[alert.status] || ''}`}>{alert.status}</span></td>
                  <td style={{ fontSize: 12 }}>{alert.assigned_to || <span style={{ color: 'var(--sm-muted)' }}>Unassigned</span>}</td>
                  <td style={{ maxWidth: 200, fontSize: 12, color: 'var(--sm-muted)' }}>{alert.alert_reason}</td>
                  <td style={{ fontSize: 12, color: 'var(--sm-muted)', whiteSpace: 'nowrap' }}>{fmtDate(alert.created_at)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {alert.status === 'open' && (
                        <button
                          className="sm-btn sm-btn-outline"
                          style={{ padding: '3px 8px', fontSize: 11 }}
                          onClick={() => handleStatus(alert, 'acknowledged')}
                          disabled={actionLoading}
                          title="Acknowledge"
                        >
                          <FiCheckCircle size={12} /> Ack
                        </button>
                      )}
                      {alert.status !== 'resolved' && (
                        <button
                          className="sm-btn sm-btn-ghost"
                          style={{ padding: '3px 8px', fontSize: 11 }}
                          onClick={() => handleStatus(alert, 'resolved')}
                          disabled={actionLoading}
                          title="Resolve"
                        >
                          Resolve
                        </button>
                      )}
                      <button
                        className="sm-btn sm-btn-ghost"
                        style={{ padding: '3px 8px', fontSize: 11 }}
                        onClick={() => { setAssignModal(alert); setAssignName(alert.assigned_to || ''); }}
                        title="Assign"
                      >
                        <FiUser size={12} /> Assign
                      </button>
                      <button
                        className="sm-btn sm-btn-primary"
                        style={{ padding: '3px 8px', fontSize: 11 }}
                        onClick={() => onOpenWorkflow(alert.alert_id)}
                        title="View Workflow"
                      >
                        <FiGitBranch size={12} /> Workflow
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Assign Modal */}
      {assignModal && (
        <div className="sm-modal-overlay" onClick={() => setAssignModal(null)}>
          <div className="sm-modal" onClick={e => e.stopPropagation()}>
            <h3 className="sm-modal-title">Assign Alert</h3>
            <p style={{ fontSize: 13, color: 'var(--sm-muted)', margin: 0 }}>
              Alert: <strong>{assignModal.alert_id?.slice(0, 20)}…</strong> | Customer: <strong>{assignModal.customer_id}</strong>
            </p>
            <div className="sm-field">
              <label>Assign To</label>
              <input
                type="text"
                value={assignName}
                onChange={e => setAssignName(e.target.value)}
                placeholder="Agent name or team"
                autoFocus
              />
            </div>
            <div className="sm-modal-footer">
              <button className="sm-btn sm-btn-ghost" onClick={() => setAssignModal(null)}>Cancel</button>
              <button className="sm-btn sm-btn-primary" onClick={handleAssign} disabled={actionLoading || !assignName.trim()}>
                {actionLoading ? 'Assigning…' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
